import { COOP_LEAF_POLICIES } from '@/features/content/components/RejectWithPoliciesDialog';
import type { HistoryRiskIndicator } from './combinedRiskDisplay';
import { pickHistoryRiskIndicators } from './combinedRiskDisplay';
import type { LabRunResult } from '../labApi';
import type { ModerationIntelligence } from '../types';

/** TipMi Pass-2 bands after deep (ModerationPublicationProperties defaults). */
const APPROVE_LT = 0.5;
const FRONTIER_LT = 0.6;
const HUMAN_LT = 0.7;

export type LabDeepVerdict =
  | 'Approved'
  | 'Frontier review'
  | 'Human review'
  | 'Rejected'
  | 'Incomplete'
  | 'Failed'
  | 'Unknown';

const REASON_TO_POLICY: Record<string, (typeof COOP_LEAF_POLICIES)[number]> = {
  incitement: 'Hate Speech',
  dehumanization: 'Hate Speech',
  hateful_imagery: 'Hate Speech',
  targeted_harassment: 'Harassment / Bullying',
  inciting_harassment: 'Harassment / Bullying',
  self_harm_suicide: 'Self-Harm & Suicide',
  unwanted_nsfw: 'Sexual / Explicit Content',
  graphic_content: 'Sexual / Explicit Content',
  adult_content: 'Sexual / Explicit Content',
  extreme_gore: 'Sexual / Explicit Content',
  child_sexual_exploitation: 'Child Safety',
  child_pornography: 'Child Safety',
  underage: 'Child Safety',
  violent_threats: 'Violence / Threats',
  weapons: 'Weapons & Dangerous Goods',
  drugs: 'Illicit Substances',
  spam: 'Spam',
  impersonation: 'Impersonation / Fake Accounts',
  copyright: 'Copyright / Intellectual Property',
  privacy: 'Privacy & Doxxing',
  doxxing: 'Privacy & Doxxing',
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Prefer package.vectors; else derive a TipMi-shaped snapshot from gpu_moderation. */
export function vectorsFromLabPackage(
  pkg: Record<string, unknown> | null | undefined,
): Record<string, Record<string, number>> | null {
  const direct = asRecord(pkg?.vectors);
  if (direct) {
    const out: Record<string, Record<string, number>> = {};
    for (const [channel, terms] of Object.entries(direct)) {
      const termMap = asRecord(terms);
      if (!termMap) continue;
      const nums: Record<string, number> = {};
      for (const [k, v] of Object.entries(termMap)) {
        const n = finiteNumber(v);
        if (n != null) nums[k] = n;
      }
      if (Object.keys(nums).length) out[channel] = nums;
    }
    if (Object.keys(out).length) return out;
  }

  const gpu = asRecord(pkg?.gpu_moderation) ?? asRecord(asRecord(pkg?.result)?.gpu_moderation);
  if (!gpu) return null;

  const moderation: Record<string, number> = {};
  const risk = finiteNumber(gpu.risk_score);
  if (risk != null) {
    moderation.combined_risk = risk;
    moderation.gpu_risk = risk;
  }
  const visualMap: Record<string, string> = {
    visual_nudity_score: 'visual_nudity_gpu',
    visual_violence_score: 'visual_violence_gpu',
    visual_csam_score: 'visual_csam_gpu',
    visual_hate_score: 'visual_hate_score',
  };
  for (const [src, dest] of Object.entries(visualMap)) {
    const n = finiteNumber(gpu[src]);
    if (n != null) moderation[dest] = n;
  }

  const findings = Array.isArray(gpu.findings) ? gpu.findings : [];
  for (const raw of findings) {
    const finding = asRecord(raw);
    if (!finding) continue;
    const reason = String(finding.reason_id ?? finding.reasonId ?? '').trim();
    const score = finiteNumber(finding.score) ?? finiteNumber(finding.confidence);
    if (!reason || score == null) continue;
    const key = reason.startsWith('reason:') ? reason : `reason:${reason}`;
    moderation[key] = Math.max(moderation[key] ?? 0, score);
  }

  const confidences: Record<string, number> = Object.fromEntries(
    COOP_LEAF_POLICIES.map((p) => [p, 0]),
  );
  confidences['Sexual / Explicit Content'] = Math.max(
    confidences['Sexual / Explicit Content'] ?? 0,
    moderation.visual_nudity_gpu ?? 0,
  );
  confidences['Violence / Threats'] = Math.max(
    confidences['Violence / Threats'] ?? 0,
    moderation.visual_violence_gpu ?? 0,
  );
  confidences['Child Safety'] = Math.max(
    confidences['Child Safety'] ?? 0,
    moderation.visual_csam_gpu ?? 0,
  );
  confidences['Hate Speech'] = Math.max(
    confidences['Hate Speech'] ?? 0,
    moderation.visual_hate_score ?? 0,
  );

  for (const [term, score] of Object.entries(moderation)) {
    if (!term.startsWith('reason:')) continue;
    const reasonId = term.slice('reason:'.length);
    const policy = REASON_TO_POLICY[reasonId];
    if (!policy) continue;
    confidences[policy] = Math.max(confidences[policy] ?? 0, score);
  }

  const policyMax = Math.max(...Object.values(confidences), 0);
  moderation.policy_max_confidence = policyMax;

  return {
    moderation,
    policy_confidence: {
      ...confidences,
      policy_max_confidence: policyMax,
    },
  };
}

export function intelligenceFromLabResult(
  result: LabRunResult | undefined,
): ModerationIntelligence | null {
  if (!result?.resultJson) return null;
  const pkg = result.resultJson;
  const vectors = vectorsFromLabPackage(pkg);
  const gpu =
    asRecord(pkg.gpu_moderation) ?? asRecord(asRecord(pkg.result)?.gpu_moderation) ?? {};
  const models = Array.isArray(gpu.models) ? gpu.models : [];
  const modelId =
    (typeof gpu.model === 'string' && gpu.model) ||
    (typeof models[0] === 'string' ? models[0] : null);
  const source = typeof gpu.source === 'string' ? gpu.source : null;
  const isCloud =
    source === 'cloud_deep' ||
    (typeof modelId === 'string' &&
      /openai|luna|bedrock|anthropic|claude|nova|gpt-/i.test(modelId));

  return {
    case_id: '',
    case_status: 'completed',
    verdict: null,
    verdict_reason: null,
    vectors,
    signals: [],
    coop_url: '',
    deep_scan_duration_ms: result.durationMs ?? null,
    gpu_model: isCloud ? null : modelId,
    frontier_model: isCloud ? modelId : null,
  };
}

export function labRiskIndicators(result: LabRunResult | undefined): HistoryRiskIndicator[] {
  return pickHistoryRiskIndicators(intelligenceFromLabResult(result));
}

export function labDeepVerdict(result: LabRunResult | undefined): {
  label: LabDeepVerdict;
  detail: string;
  risk: number | null;
} {
  if (!result) {
    return { label: 'Unknown', detail: 'No engine result', risk: null };
  }
  if (result.status === 'FAILED' || result.resultJson?.ok === false) {
    return {
      label: 'Failed',
      detail: result.errorMessage || String(result.resultJson?.error ?? 'Engine run failed'),
      risk: null,
    };
  }
  const gpu =
    asRecord(result.resultJson?.gpu_moderation) ??
    asRecord(asRecord(result.resultJson?.result)?.gpu_moderation);
  if (!gpu) {
    return { label: 'Unknown', detail: 'Missing gpu_moderation package', risk: null };
  }
  const status = String(gpu.analysis_status ?? '');
  const risk = finiteNumber(gpu.risk_score);
  if (status && status !== 'complete') {
    return {
      label: 'Incomplete',
      detail: `analysis_status=${status}`,
      risk,
    };
  }
  if (risk == null) {
    return { label: 'Unknown', detail: 'No risk_score', risk: null };
  }
  // TipMi Pass-2 decide bands (same defaults as ModerationPublicationProperties).
  if (risk < APPROVE_LT) {
    return {
      label: 'Approved',
      detail: `TipMi clear band (risk ${risk.toFixed(3)} < ${APPROVE_LT})`,
      risk,
    };
  }
  if (risk < FRONTIER_LT) {
    return {
      label: 'Frontier review',
      detail: `TipMi frontier band (${APPROVE_LT} ≤ risk ${risk.toFixed(3)} < ${FRONTIER_LT})`,
      risk,
    };
  }
  if (risk < HUMAN_LT) {
    return {
      label: 'Human review',
      detail: `TipMi human band (${FRONTIER_LT} ≤ risk ${risk.toFixed(3)} < ${HUMAN_LT})`,
      risk,
    };
  }
  return {
    label: 'Rejected',
    detail: `TipMi reject band (risk ${risk.toFixed(3)} ≥ ${HUMAN_LT})`,
    risk,
  };
}

export function verdictBadgeClass(label: LabDeepVerdict): string {
  switch (label) {
    case 'Approved':
      return 'border-green-200 bg-green-100 text-green-800';
    case 'Frontier review':
      return 'border-sky-200 bg-sky-100 text-sky-800';
    case 'Human review':
      return 'border-yellow-200 bg-yellow-100 text-yellow-800';
    case 'Rejected':
      return 'border-red-200 bg-red-100 text-red-800';
    case 'Incomplete':
    case 'Failed':
      return 'border-orange-200 bg-orange-100 text-orange-800';
    default:
      return 'border-border bg-muted text-muted-foreground';
  }
}
